import * as geom from "js/core/utilities/geom";
import logger from "js/core/logger";
import { getBase64Image, isObjectDiff, blobToDataURL } from "js/core/utilities/utilities";
import { TextElement } from "js/elements/base/TextElement";
import {
    SVGElement,
    SVGGroupElement
} from "js/elements/base/SVGElement";
import { $, _, tinycolor, PptxGenJS } from "js/vendor";
import { ContentElement } from "js/elements/base/ContentElement";
import {
    AssetType,
    CellChangeStyle,
    AuthoringShapeType,
    FormatType,
    AuthoringBlockType,
    TextStyleType,
    BackgroundStyleType,
    CANVAS_WIDTH,
    CANVAS_HEIGHT,
    HorizontalAlignType
} from "common/constants";
import { ds } from "js/core/models/dataService";
import Logos from "js/core/models/logos";
import { app } from "js/namespaces";
import { ShowDialog, ShowErrorDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import ExportToPPTWarningDialog from "js/react/views/PresentationSettings/dialogs/ExportToPPTWarningDialog";
import Dropbox from "js/core/utilities/dropbox";
import { ELEMENT_IDS, TextStyleEnum } from "common/constants";
import { DecorationElement, DecorationType } from "js/elements/base/DecorationElement";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import { getSelectionState } from "js/editor/elementUI/authoring/Editors/BlockEditors/TextHelpers";
import { loadFileAsDataUrl } from "js/core/utilities/promiseHelper";
import PolyLineExporter from "./PolyLineExporter";
import { BackgroundImage } from "js/elements/base/CanvasBackground";

// issues
// ---------------------------------------------------------
// color alpha is not supported (text fontColor opacity, etc.)

const SLIDE_WIDTH = 13.33;

const MULT = SLIDE_WIDTH / CANVAS_WIDTH;

const log = logger.info(logger.EXPORT).bind(logger);

/**
 * Removes non-printable characters from string
 */
function sanitizeString(string) {
    return string.replace(
        // eslint-disable-next-line no-misleading-character-class
        /[\0-\x1F\x7F-\x9F\xAD\u0378\u0379\u037F-\u0383\u038B\u038D\u03A2\u0528-\u0530\u0557\u0558\u0560\u0588\u058B-\u058E\u0590\u05C8-\u05CF\u05EB-\u05EF\u05F5-\u0605\u061C\u061D\u06DD\u070E\u070F\u074B\u074C\u07B2-\u07BF\u07FB-\u07FF\u082E\u082F\u083F\u085C\u085D\u085F-\u089F\u08A1\u08AD-\u08E3\u08FF\u0978\u0980\u0984\u098D\u098E\u0991\u0992\u09A9\u09B1\u09B3-\u09B5\u09BA\u09BB\u09C5\u09C6\u09C9\u09CA\u09CF-\u09D6\u09D8-\u09DB\u09DE\u09E4\u09E5\u09FC-\u0A00\u0A04\u0A0B-\u0A0E\u0A11\u0A12\u0A29\u0A31\u0A34\u0A37\u0A3A\u0A3B\u0A3D\u0A43-\u0A46\u0A49\u0A4A\u0A4E-\u0A50\u0A52-\u0A58\u0A5D\u0A5F-\u0A65\u0A76-\u0A80\u0A84\u0A8E\u0A92\u0AA9\u0AB1\u0AB4\u0ABA\u0ABB\u0AC6\u0ACA\u0ACE\u0ACF\u0AD1-\u0ADF\u0AE4\u0AE5\u0AF2-\u0B00\u0B04\u0B0D\u0B0E\u0B11\u0B12\u0B29\u0B31\u0B34\u0B3A\u0B3B\u0B45\u0B46\u0B49\u0B4A\u0B4E-\u0B55\u0B58-\u0B5B\u0B5E\u0B64\u0B65\u0B78-\u0B81\u0B84\u0B8B-\u0B8D\u0B91\u0B96-\u0B98\u0B9B\u0B9D\u0BA0-\u0BA2\u0BA5-\u0BA7\u0BAB-\u0BAD\u0BBA-\u0BBD\u0BC3-\u0BC5\u0BC9\u0BCE\u0BCF\u0BD1-\u0BD6\u0BD8-\u0BE5\u0BFB-\u0C00\u0C04\u0C0D\u0C11\u0C29\u0C34\u0C3A-\u0C3C\u0C45\u0C49\u0C4E-\u0C54\u0C57\u0C5A-\u0C5F\u0C64\u0C65\u0C70-\u0C77\u0C80\u0C81\u0C84\u0C8D\u0C91\u0CA9\u0CB4\u0CBA\u0CBB\u0CC5\u0CC9\u0CCE-\u0CD4\u0CD7-\u0CDD\u0CDF\u0CE4\u0CE5\u0CF0\u0CF3-\u0D01\u0D04\u0D0D\u0D11\u0D3B\u0D3C\u0D45\u0D49\u0D4F-\u0D56\u0D58-\u0D5F\u0D64\u0D65\u0D76-\u0D78\u0D80\u0D81\u0D84\u0D97-\u0D99\u0DB2\u0DBC\u0DBE\u0DBF\u0DC7-\u0DC9\u0DCB-\u0DCE\u0DD5\u0DD7\u0DE0-\u0DF1\u0DF5-\u0E00\u0E3B-\u0E3E\u0E5C-\u0E80\u0E83\u0E85\u0E86\u0E89\u0E8B\u0E8C\u0E8E-\u0E93\u0E98\u0EA0\u0EA4\u0EA6\u0EA8\u0EA9\u0EAC\u0EBA\u0EBE\u0EBF\u0EC5\u0EC7\u0ECE\u0ECF\u0EDA\u0EDB\u0EE0-\u0EFF\u0F48\u0F6D-\u0F70\u0F98\u0FBD\u0FCD\u0FDB-\u0FFF\u10C6\u10C8-\u10CC\u10CE\u10CF\u1249\u124E\u124F\u1257\u1259\u125E\u125F\u1289\u128E\u128F\u12B1\u12B6\u12B7\u12BF\u12C1\u12C6\u12C7\u12D7\u1311\u1316\u1317\u135B\u135C\u137D-\u137F\u139A-\u139F\u13F5-\u13FF\u169D-\u169F\u16F1-\u16FF\u170D\u1715-\u171F\u1737-\u173F\u1754-\u175F\u176D\u1771\u1774-\u177F\u17DE\u17DF\u17EA-\u17EF\u17FA-\u17FF\u180F\u181A-\u181F\u1878-\u187F\u18AB-\u18AF\u18F6-\u18FF\u191D-\u191F\u192C-\u192F\u193C-\u193F\u1941-\u1943\u196E\u196F\u1975-\u197F\u19AC-\u19AF\u19CA-\u19CF\u19DB-\u19DD\u1A1C\u1A1D\u1A5F\u1A7D\u1A7E\u1A8A-\u1A8F\u1A9A-\u1A9F\u1AAE-\u1AFF\u1B4C-\u1B4F\u1B7D-\u1B7F\u1BF4-\u1BFB\u1C38-\u1C3A\u1C4A-\u1C4C\u1C80-\u1CBF\u1CC8-\u1CCF\u1CF7-\u1CFF\u1DE7-\u1DFB\u1F16\u1F17\u1F1E\u1F1F\u1F46\u1F47\u1F4E\u1F4F\u1F58\u1F5A\u1F5C\u1F5E\u1F7E\u1F7F\u1FB5\u1FC5\u1FD4\u1FD5\u1FDC\u1FF0\u1FF1\u1FF5\u1FFF\u200B-\u200F\u202A-\u202E\u2060-\u206F\u2072\u2073\u208F\u209D-\u209F\u20BB-\u20CF\u20F1-\u20FF\u218A-\u218F\u23F4-\u23FF\u2427-\u243F\u244B-\u245F\u2700\u2B4D-\u2B4F\u2B5A-\u2BFF\u2C2F\u2C5F\u2CF4-\u2CF8\u2D26\u2D28-\u2D2C\u2D2E\u2D2F\u2D68-\u2D6E\u2D71-\u2D7E\u2D97-\u2D9F\u2DA7\u2DAF\u2DB7\u2DBF\u2DC7\u2DCF\u2DD7\u2DDF\u2E3C-\u2E7F\u2E9A\u2EF4-\u2EFF\u2FD6-\u2FEF\u2FFC-\u2FFF\u3040\u3097\u3098\u3100-\u3104\u312E-\u3130\u318F\u31BB-\u31BF\u31E4-\u31EF\u321F\u32FF\u4DB6-\u4DBF\u9FCD-\u9FFF\uA48D-\uA48F\uA4C7-\uA4CF\uA62C-\uA63F\uA698-\uA69E\uA6F8-\uA6FF\uA78F\uA794-\uA79F\uA7AB-\uA7F7\uA82C-\uA82F\uA83A-\uA83F\uA878-\uA87F\uA8C5-\uA8CD\uA8DA-\uA8DF\uA8FC-\uA8FF\uA954-\uA95E\uA97D-\uA97F\uA9CE\uA9DA-\uA9DD\uA9E0-\uA9FF\uAA37-\uAA3F\uAA4E\uAA4F\uAA5A\uAA5B\uAA7C-\uAA7F\uAAC3-\uAADA\uAAF7-\uAB00\uAB07\uAB08\uAB0F\uAB10\uAB17-\uAB1F\uAB27\uAB2F-\uABBF\uABEE\uABEF\uABFA-\uABFF\uD7A4-\uD7AF\uD7C7-\uD7CA\uD7FC-\uF8FF\uFA6E\uFA6F\uFADA-\uFAFF\uFB07-\uFB12\uFB18-\uFB1C\uFB37\uFB3D\uFB3F\uFB42\uFB45\uFBC2-\uFBD2\uFD40-\uFD4F\uFD90\uFD91\uFDC8-\uFDEF\uFDFE\uFDFF\uFE1A-\uFE1F\uFE27-\uFE2F\uFE53\uFE67\uFE6C-\uFE6F\uFE75\uFEFD-\uFF00\uFFBF-\uFFC1\uFFC8\uFFC9\uFFD0\uFFD1\uFFD8\uFFD9\uFFDD-\uFFDF\uFFE7\uFFEF-\uFFFB\uFFFE\uFFFF]/g,
        "");
}

class ExportToPPT {
    static EXPORT_DESTINATION = {
        local: "local",
        dropbox: "dropbox",
        base64: "base64",
        blob: "blob"
    }

    constructor(options = {}) {
        this.options = options;
    }

    async export({
        slideCanvases = null,
        debug = false,
        destination = ExportToPPT.EXPORT_DESTINATION.local,
        includeSkippedSlides,
        showDialogs = true,
        exportSlidesAsImages = false
    }) {
        const pptx = new PptxGenJS();
        pptx.setLayout("LAYOUT_WIDE");

        let progressDialog;
        if (showDialogs) {
            progressDialog = ShowDialog(ProgressDialog, {
                title: "Exporting to PPTX",
                onCancel: () => {
                    this.isCanceled = true;
                    progressDialog.props.closeDialog();
                }
            });
        }

        const failedSlideIndexes = [];
        const exportWarnings = [];

        // make sure the canvas_bounds is not display:none or svg nodes won't have a bbox
        const canvasBoundsDisplay = $("#canvas_bounds").css("display");
        if (canvasBoundsDisplay === "none") {
            $("#canvas_bounds").css("display", "block");
        }

        // layouting the canvases so svg nodes have correct bboxes
        await app.mainView.editorView.layout(false);

        // Slide canvases that we'll be exporting
        let slideCanvasesToExport = slideCanvases || app.mainView.editorView.slides.map(slide => slide.canvas);
        // isExport is used to prevent animated connectors from refreshing canvas
        // and pictures from reloading during export
        slideCanvasesToExport.forEach(slideCanvas => slideCanvas.isExport = true);

        // check if filtering out
        if (!includeSkippedSlides) {
            slideCanvasesToExport = slideCanvasesToExport.filter(slide => !slide.dataModel?.attributes?.isSkipped);
        }

        for (let i = 0; i < slideCanvasesToExport.length; i++) {
            if (this.isCanceled) {
                break;
            }

            const slideCanvas = slideCanvasesToExport[i];

            try {
                await slideCanvas.renderSlide(false);
                await this.waitForRender();

                if (this.isCanceled) {
                    break;
                }

                log("exporting slide " + slideCanvasesToExport.indexOf(slideCanvas) + " of " + slideCanvasesToExport.length);
                if (showDialogs) {
                    progressDialog.setProgress((slideCanvasesToExport.indexOf(slideCanvas) / slideCanvasesToExport.length) * 100);
                }

                if (exportSlidesAsImages) {
                    const urls = await slideCanvas.dataModel.generateJpegs();
                    for (const url of urls) {
                        const imageBlob = await fetch(url)
                            .then(response => response.blob());
                        const base64image = await loadFileAsDataUrl(imageBlob);

                        const slide = pptx.addNewSlide();
                        slide.addImage({
                            data: base64image,
                            x: 0,
                            y: 0,
                            w: 1280 * 13.33 / 1280,
                            h: 720 * 13.33 / 1280,
                        });
                    }
                } else {
                    if (slideCanvas.playbackStages.length > 0) {
                        const currentPlaybackStage = slideCanvas.currentPlaybackStage;
                        await slideCanvas.resetPlayback(false);
                        for (const stage of slideCanvas.playbackStages) {
                            const slide = pptx.addNewSlide();
                            const canvasExporter = new CanvasExporter(pptx, slideCanvas);
                            await canvasExporter.export(
                                slide,
                                exportWarnings,
                                debug
                            );
                            await slideCanvas.playNextStage(false);
                        }
                        await slideCanvas.goToStage(currentPlaybackStage, false);
                    } else {
                        const slide = pptx.addNewSlide();
                        const canvasExporter = new CanvasExporter(pptx, slideCanvas);
                        await canvasExporter.export(
                            slide,
                            exportWarnings,
                            debug
                        );
                    }
                }
            } catch (err) {
                failedSlideIndexes.push(i);
                logger.error(err);
            }
        }

        const finishExport = () => {
            slideCanvasesToExport.forEach(slideCanvas => slideCanvas.isExport = false);
            return app.currentCanvas.refreshCanvas();
        };

        const response = {
            failedSlideIndexes,
            exportWarnings,
            isCanceled: false
        };

        if (this.isCanceled) {
            await finishExport();
            return { ...response, isCanceled: true };
        }

        if (failedSlideIndexes.length !== slideCanvasesToExport.length) {
            const showWarningDialogs = () => {
                if (failedSlideIndexes.length > 0 || exportWarnings.length > 0) {
                    ShowDialog(ExportToPPTWarningDialog, { failedSlideIndexes, exportWarnings });
                }
            };

            const fileName = ds.selection.presentation.get("name");

            if (destination === ExportToPPT.EXPORT_DESTINATION.dropbox) {
                const blob = await new Promise(resolve => pptx.save("jszip", resolve, "blob"));
                try {
                    const dropboxResp = await Dropbox.saveFileFromBlob(blob, `${fileName}.pptx`, "application/vnd.openxmlformats-officedocument.presentationml.presentation");
                    if (dropboxResp.cancelled) {
                        const message = "Seems like you cancelled the process";
                        if (showDialogs) {
                            ShowErrorDialog({ title: "Error", message });
                        } else {
                            throw new Error(message);
                        }
                    } else {
                        if (showDialogs) {
                            showWarningDialogs();
                            ShowMessageDialog({
                                title: "Success",
                                message: "The presentation was successfully saved in your Dropbox"
                            });
                        }
                    }
                } catch (err) {
                    logger.error(err);

                    let message;
                    if (err.message.startsWith("Not enough space")) {
                        message = "Sorry, we could not save the presentation because your Dropbox is out of space";
                    } else if (err.message.startsWith("Failed to open/load the window")) {
                        message = "Sorry, we could not save the presentation into your Dropbox because the popup was blocked by your browser. Allow popups for Beautiful.ai and try again";
                    } else {
                        message = "Sorry, we could not save the presentation into your Dropbox";
                    }

                    if (showDialogs) {
                        ShowErrorDialog({ title: "Error", message });
                    } else {
                        throw new Error(message);
                    }
                }
            } else if (destination === ExportToPPT.EXPORT_DESTINATION.local) {
                await new Promise(resolve => pptx.save(fileName, resolve));
                if (showDialogs) {
                    showWarningDialogs();
                }
            } else if (destination === ExportToPPT.EXPORT_DESTINATION.base64) {
                const blob = await new Promise(resolve => pptx.save("jszip", resolve, "blob"));
                response.base64Data = await blobToDataURL(blob);
                if (showDialogs) {
                    showWarningDialogs();
                }
            } else if (destination === ExportToPPT.EXPORT_DESTINATION.blob) {
                response.blob = await new Promise(resolve => pptx.save("jszip", resolve, "blob"));
                response.fileName = fileName;
                if (showDialogs) {
                    showWarningDialogs();
                }
            }
        } else {
            if (showDialogs) {
                ShowErrorDialog({
                    error: "Unable to export presentation",
                    message: "Sorry, something has gone really wrong exporting this presentation. Please contact us at" +
                        " support@beautiful.ai for more help."
                });
            } else {
                throw new Error("Failed to export presentation");
            }
        }

        if (showDialogs) {
            progressDialog.props.closeDialog();
        }

        await finishExport();

        // restore the canvas_bounds display setting immediately
        $("#canvas_bounds").css("display", canvasBoundsDisplay);

        return response;
    }

    async waitForRender() {
        return new Promise(resolve => {
            setTimeout(() => {
                log("timeout");
                resolve();
            }, 0);
        });
    }
}

class CanvasExporter {
    convertCoordinate(val) {
        return val * MULT;
    }

    convertRectToSlide(rect) {
        let width = this.convertCoordinate(rect.width);
        let height = this.convertCoordinate(rect.height);
        if (width < 0) {
            width = 0.1;
        }
        if (height < 0) {
            height = 0.1;
        }
        return {
            x: this.convertCoordinate(rect.left),
            y: this.convertCoordinate(rect.top),
            w: width,
            h: height
        };
    }

    convertPointToSlide(point) {
        return {
            x: this.convertCoordinate(point.x),
            y: this.convertCoordinate(point.y)
        };
    }

    getColorFromRGB(rgb) {
        if (rgb.contains("rgba")) {
            return {
                type: "solid",
                color: tinycolor(rgb)
                    .toHexString()
                    .substr(1),
                alpha: parseInt(100 - tinycolor(rgb)._a * 100)
            };
        } else if (rgb == "transparent" || rgb == "none") {
            return null;
        } else {
            return tinycolor(rgb)
                .toHexString()
                .substr(1);
        }
    }

    getSVGElementFillColor(svg) {
        let fill = svg.style.fill || svg.getAttribute("fill");
        let fillOpacity = svg.style.fillOpacity || svg.getAttribute("fill-opacity") || 1;

        if (fillOpacity == 1) {
            return this.getColorFromRGB(fill);
        } else {
            return {
                type: "solid",
                color: tinycolor(fill)
                    .toHexString()
                    .substr(1),
                alpha: parseInt(100 - fillOpacity * 100)
            };
        }
    }

    constructor(pptx, canvas) {
        this.pptx = pptx;
        this.canvas = canvas;
    }

    addWarning(warning) {
        if (!this.exportWarnings.contains(warning)) {
            this.exportWarnings.push(warning);
        }
    }

    async export(slide, exportWarnings, debug = false) {
        this.slide = slide;
        this.debug = debug;
        this.exportWarnings = exportWarnings;
        this.slideNotes = [];

        this.canvasBounds = geom.Rect.FromBoundingClientRect(this.canvas.el.getBoundingClientRect());

        this.slide.back = this.canvas
            .getBackgroundColor()
            .toHexString()
            .substr(1);

        if (this.canvas.slideTemplate.constructor.id == "slidePlaceholder") {
            this.slide.back = "ffffff";
        }

        if (this.canvas.hasSlideNotes()) {
            this.slideNotes.push(this.canvas.getNormalizedSlideNotes());
        }

        // Sorting elements by layer
        for (const element of _.sortBy(this.canvas.layouter.elements, element => element.calculatedProps?.layer)) {
            await this.exportElement(element, slide);
        }

        if (this.slideNotes.length > 0) {
            this.slide.addNotes(this.slideNotes.join("\n"));
        }
    }

    async exportElement(element, slide) {
        if (element.options.preventExport) return;
        if (!element.DOMNode || element.DOMNode.style.display === "none") return;
        if (element.calculatedProps?.opacity === 0) return;

        log(`exporting ${element.type}, path: ${element.uniquePath}`);

        if (element.decoration && element.decoration.hasBackground) {
            await this.exportDecoration(element.decoration, "background");
        }

        if (element.type === "VignetteOverlayElement") {
            // Won't be exporting vignette overlay
            return;
        } else if (element.type === "BorderOverlayElement") {
            return this.exportBorderOverlay(element);
        } else if (element.type === "CanvasBackground") {
            return this.exportCanvasBackground(element);
        } else if (element instanceof DecorationElement) {
            // Do nothing
        } else if (element instanceof TextElement) {
            await this.exportText(element, slide);
        } else if (element instanceof ContentElement) {
            await this.exportContent(element);
        } else if (element instanceof SVGElement && !(element instanceof SVGGroupElement)) {
            switch (element.type) {
                case "SVGPolylineElement":
                case "AuthoringPathElement":
                    this.exportSVGPolyline(element);
                    break;
                case "SVGCircleElement":
                    this.exportSVGShape(element, this.pptx.shapes.OVAL);
                    break;
                case "SVGRectElement":
                    this.exportSVGShape(element, this.pptx.shapes.RECTANGLE);
                    break;
                default:
                    if (element.exportAsImage || element.options.exportAsImage) {
                        await this.exportSVGNodeAsImage(element.DOMNode, element.canvasBounds);
                    } else {
                        await this.exportSVGNode(element);
                    }
            }
            return;
        } else if (element.type === "ConnectorItem") {
            await this.exportSVGNode(element, element.pathNode);
            if (element.startDecorationNode) {
                await this.exportSVGNode(element, element.startDecorationNode);
            }
            if (element.endDecorationNode) {
                await this.exportSVGNode(element, element.endDecorationNode);
            }
        } else if (element.type === "ImageFrame") {
            this.slide.addImage({
                path: element.frameURL,
                ...this.convertRectToSlide(element.canvasBounds)
            });
        } else if (element.type === "WordCloudElement") {
            return this.exportWordCloud(element);
        } else if (element.type === "Footer") {
            await this.exportFooter(element);
        } else if (element.type === "StreetMap") {
            return this.exportStreetMap(element);
        } else if (element.type === "OrgChartConnectors") {
            return this.exportOrgChartConnectors(element);
        } else if (element.type === "Icon") {
            // Not exporting Icon elements w/o icons
            if (element.iconPath) {
                await this.exportSVGNode(element, element.DOMNode.firstElementChild.querySelector("path"));
            }
        } else if (element.type === "Video") {
            this.hasVideo = true;
            return this.exportVideo(element);
        } else if (element.type === "TableFrame") {
            return this.exportTable(element);
        } else if (element.type === "Chart") {
            // element.annotations && element.annotations.svg.hide();
            // await this.exportSVGNodeAsImage(element.svg.select(".highcharts-root").members[0].node, element.canvasBounds);
            await this.exportSVGNodeAsImage(element.DOMNode.querySelector(".highcharts-root"), element.canvasBounds);
            // element.annotations && element.annotations.svg.show();
        } else if (element.type === "Picture") {
            // skip - handled by contentelement
        } else if (element.type === "Logo") {
            // skip - handled by contentelement
        } else if (element.type === "AuthoringShapeElement") {
            return this.exportAuthoringShape(element);
        } else if (element.type == "WebView") {
            this.addWarning("WebView is not supported");
            return;
        } else if (Object.values(element.elements).filter(element => !element.options.preventExport).length === 0 && element.DOMNode.childElementCount === 1 && element.DOMNode.firstElementChild.nodeName === "svg") {
            // catch for custom svg elements
            await this.exportSVGNode(element);
        }

        for (let childElement of _.sortBy(element.elements, element => element.calculatedProps?.layer)) {
            if (childElement === element.decoration && element.decoration?.hasForeground) {
                await this.exportDecoration(element.decoration, "foreground");
            } else {
                await this.exportElement(childElement, slide);
            }
        }

        // NOTE: Moved commented block below into childElement loop above
        //   so that decorations are rendered in the proper order.
        //
        //   Leaving this here in case this change breaks something.
        //   Delete this commented code if it's been several months without issue.

        // if (element.decoration && element.decoration.hasForeground) {
        //     await this.exportDecoration(element.decoration, "foreground");
        // }
    }

    async exportDecoration(decorationElement, postition) {
        const ref = postition === "background" ? decorationElement.backgroundRef : decorationElement.foregroundRef;
        const domNode = ref.current;

        const { type, shape, cornerRadius } = decorationElement.styles;
        if (type === "svg" || shape === DecorationType.OCTAGON) {
            await this.exportSVGNode(decorationElement, domNode);
        } else if (shape === DecorationType.RECT || shape === DecorationType.CIRCLE) {
            let bounds = decorationElement.bounds;
            let pptShape;
            if (shape === DecorationType.RECT) {
                pptShape = cornerRadius ? this.pptx.shapes.ROUNDED_RECTANGLE : this.pptx.shapes.RECTANGLE;
            } else if (shape === DecorationType.CIRCLE) {
                pptShape = this.pptx.shapes.OVAL;
                bounds = decorationElement.bounds.square().centerInRect(decorationElement.bounds);
            }

            const shapeOptions = {
                ...this.convertRectToSlide(
                    bounds
                        .offset(decorationElement.canvasBounds.position)
                        .offset(-decorationElement.parentElement.styles.paddingLeft || 0, -decorationElement.parentElement.styles.paddingTop || 0)
                )
            };

            if (domNode.style.backgroundColor) {
                shapeOptions.fill = this.getColorFromRGB(domNode.style.backgroundColor);
            }

            if (domNode.style.borderColor && domNode.style.borderColor !== "none" && parseFloat(domNode.style.borderWidth) > 0) {
                shapeOptions.line = this.getColorFromRGB(domNode.style.borderColor);
                shapeOptions.lineSize = parseFloat(domNode.style.borderWidth);
                switch (domNode.style.borderStyle) {
                    case "dashed":
                        shapeOptions.lineDash = "dash";
                        break;
                    default:
                        shapeOptions.lineDash = "solid";
                        break;
                }
            }

            if (cornerRadius && shape === DecorationType.RECT) {
                shapeOptions.rectRadius = cornerRadius / 100;
            }

            this.slide.addShape(pptShape, shapeOptions);
        }
    }

    async exportAuthoringShape(authoringShapeElement) {
        if (
            authoringShapeElement.model.fill !== "none" ||
            authoringShapeElement.model.stroke !== "none" ||
            authoringShapeElement.model.shape === "raw_svg"
        ) {
            const shapeType = authoringShapeElement.model.shape;
            const shapeNode = authoringShapeElement.DOMNode.firstElementChild.firstElementChild;
            if (shapeType === AuthoringShapeType.RECT) {
                this.exportSVGShape(authoringShapeElement, this.pptx.shapes.RECTANGLE, shapeNode);
            } else if (shapeType === AuthoringShapeType.ELLIPSE) {
                this.exportSVGShape(authoringShapeElement, this.pptx.shapes.OVAL, shapeNode);
            } else {
                await this.exportSVGNode(authoringShapeElement, shapeNode);
            }
        }

        if (authoringShapeElement.model.blocks && authoringShapeElement.model.blocks.length > 0) {
            await this.exportAuthoringBlockContainer(authoringShapeElement);
        }
    }

    async exportAuthoringBlockContainer(authoringShapeElement) {
        const authoringBlockContainer = authoringShapeElement.blockContainer;

        let textRuns = [];
        let currentTextBounds = null;

        const writeTextRuns = () => {
            if (textRuns.length > 0) {
                const textOptions = {
                    ...this.convertRectToSlide(
                        currentTextBounds
                            .offset(-this.canvasBounds.left, -this.canvasBounds.top)
                            .multiply(1 / this.canvas.canvasScale)
                    ),
                    valign: "middle",
                    margin: 0
                };

                this.slide.addText(textRuns, textOptions);

                textRuns = [];
            }

            currentTextBounds = null;
        };

        for (const block of authoringBlockContainer.blocks) {
            if (!block) {
                continue;
            }

            if (block.type === AuthoringBlockType.TEXT) {
                // Add some padding to avoid unexpected line breaks due to non perfect
                // char spacing and char widths matching
                let blockBounds = block.bounds;
                const padding = Math.max(Math.round(blockBounds.width * 0.05), 2);
                const { textAlign } = block.getTextStyles();
                if (textAlign === HorizontalAlignType.LEFT) {
                    blockBounds = blockBounds.addPadding({ left: 0, right: padding * 2, top: 0, bottom: 0 });
                } else if (textAlign === HorizontalAlignType.RIGHT) {
                    blockBounds = blockBounds.addPadding({ left: padding * 2, right: 0, top: 0, bottom: 0 });
                } else {
                    blockBounds = blockBounds.addPadding({ left: padding, right: padding, top: 0, bottom: 0 });
                }

                if (!currentTextBounds) {
                    currentTextBounds = blockBounds;
                } else {
                    currentTextBounds = currentTextBounds.union(blockBounds);
                }
                const blockTextRuns = this.getTextRunsForAthoringTextBlock(block, authoringShapeElement);
                textRuns.push(...blockTextRuns);
            } else {
                writeTextRuns();
            }

            if (block.type === AuthoringBlockType.CODE) {
                this.exportAthoringCodeBlock(block);
            } else if (block.type === AuthoringBlockType.EQUATION) {
                const backgroundOptions = {
                    ...this.convertRectToSlide(geom.Rect.FromBBox(block.ref.current.getBoundingClientRect()).offset(-this.canvasBounds.left, -this.canvasBounds.top).multiply(1 / this.canvas.canvasScale)),
                    fill: "ffffff"
                };
                this.slide.addShape(this.pptx.shapes.RECTANGLE, backgroundOptions);

                await this.exportRawSVGNode(block.ref.current.firstElementChild);
            }
        }

        writeTextRuns();
    }

    getTextRunsForAthoringTextBlock(block, authoringShapeElement) {
        const contentEditableNode = block.ref.current;

        const textContent = contentEditableNode.textContent;
        if (!textContent) {
            return [];
        }

        const isBlockEditing = block.isEditing;
        if (isBlockEditing) {
            block.onStopEditing();
        }

        // Explicitly enabling user-select on the contenteditable to allow text selection in Safari
        const userSelect = contentEditableNode.style.getPropertyValue("user-select");
        contentEditableNode.style.setProperty("user-select", "text");
        contentEditableNode.style.setProperty("-webkit-user-select", "text");

        const selection = window.getSelection();
        selection.selectAllChildren(contentEditableNode);
        selection.collapseToStart();

        const textStyles = block.getTextStyles();

        const textRuns = [];
        let stepsPerformed = 0;
        let currentTextOptions = {};
        let currentText = "";
        while (true) {
            selection.modify("extend", "forward", "character");
            const range = selection.getRangeAt(0);
            let selectedNode = range.commonAncestorContainer;
            while (selectedNode.nodeName === "#text") {
                selectedNode = selectedNode.parentElement;
            }
            const nodeStyles = window.getComputedStyle(selectedNode);

            const color = this.getColorFromRGB(document.queryCommandValue("foreColor"));
            const bold = !!document.queryCommandState("bold");
            const italic = !!document.queryCommandState("italic");
            const underline = !!document.queryCommandState("underline");
            const strike = !!document.queryCommandState("strikeThrough");
            const subscript = !!document.queryCommandState("subscript");
            const superscript = !!document.queryCommandState("superscript");
            const fontSize = Math.round(parseInt(nodeStyles.getPropertyValue("font-size").replace("px", "")) * 0.75);
            const fontFace = authoringShapeElement.fonts[block.baseFontId].label;
            const charSpacing = parseFloat(textStyles.letterSpacing * 0.75).toFixed(2);
            const lineSpacing = parseFloat(textStyles.lineHeight * fontSize).toFixed(2);

            const textOptions = {
                color,
                bold,
                italic,
                underline,
                strike,
                subscript,
                superscript,
                fontSize,
                fontFace,
                align: textStyles.textAlign,
                charSpacing,
                lineSpacing,
                break: false
            };

            // NOTE: non-printable unicode chars are sanitized by selection.toString()
            if (_.isEqual(textOptions, currentTextOptions)) {
                currentText += selection.toString();
            } else {
                if (currentText) {
                    textRuns.push({ text: currentText, options: currentTextOptions });
                }
                currentTextOptions = textOptions;
                currentText = selection.toString();
            }

            selection.collapseToEnd();

            stepsPerformed++;

            const { isAtEnd } = getSelectionState(contentEditableNode);
            // The second condition is a rough estimation to make sure we never end up in an endless cycle
            if (isAtEnd || stepsPerformed > block.model.html.length) {
                textRuns.push({ text: currentText, options: currentTextOptions });
                break;
            }
        }

        if (textRuns.length > 0) {
            if (block.textStyle === TextStyleType.NUMBERED_LIST || block.textStyle === TextStyleType.BULLET_LIST) {
                textRuns[0].options.bullet = block.textStyle === TextStyleType.NUMBERED_LIST ? { type: "number" } : true;
                textRuns[0].options.indentLevel = block.listIndent;
            }

            textRuns[0].options.break = true;

            textRuns[0].options.paraSpaceBefore = Math.round(block.blockMargin.top * 1.2);
            textRuns[textRuns.length - 1].options.paraSpaceAfter = Math.round(block.blockMargin.bottom * 1.2);
        }

        if (isBlockEditing) {
            block.onStartEditing();
        }

        // Restoring user-select to its intial state
        if (userSelect) {
            contentEditableNode.style.setProperty("user-select", userSelect);
            contentEditableNode.style.setProperty("-webkit-user-select", userSelect);
        } else {
            contentEditableNode.style.removeProperty("user-select");
            contentEditableNode.style.removeProperty("-webkit-user-select");
        }

        return textRuns;
    }

    exportAthoringCodeBlock(block) {
        const preNode = block.codeRef.current.firstElementChild;
        const textContent = preNode.textContent;
        if (!textContent) {
            return;
        }

        const userSelectAttrs = ["user-select", "-moz-user-select", "-webkit-user-select", "-ms-user-select"];
        userSelectAttrs.forEach(userSelectAttr => preNode.style.setProperty(userSelectAttr, "text"));

        const containerNode = block.codeRef.current.parentElement;
        const containerNodeStyles = window.getComputedStyle(containerNode);
        const fill = this.getColorFromRGB(containerNodeStyles.getPropertyValue("background"));

        const selection = window.getSelection();
        selection.selectAllChildren(preNode);
        selection.collapseToStart();

        const textRuns = [];
        let stepsPerformed = 0;
        let currentTextOptions = {};
        let currentText = "";
        while (true) {
            selection.modify("extend", "forward", "character");
            const range = selection.getRangeAt(0);
            let selectedNode = range.commonAncestorContainer;
            while (selectedNode.nodeName === "#text") {
                selectedNode = selectedNode.parentElement;
            }
            const nodeStyles = window.getComputedStyle(selectedNode);

            const color = this.getColorFromRGB(document.queryCommandValue("foreColor"));
            const bold = !!document.queryCommandState("bold");
            const italic = !!document.queryCommandState("italic");
            const underline = !!document.queryCommandState("underline");
            const strike = !!document.queryCommandState("strikeThrough");
            const subscript = !!document.queryCommandState("subscript");
            const superscript = !!document.queryCommandState("superscript");
            const fontSize = Math.round(parseInt(nodeStyles.getPropertyValue("font-size").replace("px", "")) * 0.75);
            // Code block uses monospace font family, which is usually mapped to Courier font by the browser
            const fontFace = "Courier";
            const lineSpacing = parseFloat(parseInt(nodeStyles.getPropertyValue("line-height")) * 0.75).toFixed(2);

            const textOptions = {
                color,
                bold,
                italic,
                underline,
                strike,
                subscript,
                superscript,
                fontSize,
                fontFace,
                lineSpacing,
                align: "left",
                break: false
            };

            // NOTE: non-printable unicode chars are sanitized by selection.toString()
            if (_.isEqual(textOptions, currentTextOptions)) {
                currentText += selection.toString();
            } else {
                if (currentText) {
                    textRuns.push({ text: currentText, options: currentTextOptions });
                }
                currentTextOptions = textOptions;
                currentText = selection.toString();
            }

            stepsPerformed++;

            selection.collapseToEnd();
            const { isAtEnd } = getSelectionState(preNode);
            // The second condition is a rough estimation to make sure we never end up in an endless cycle
            if (isAtEnd || stepsPerformed > block.model.html.length) {
                textRuns.push({ text: currentText, options: currentTextOptions });
                break;
            }
        }

        if (textRuns.length > 0) {
            textRuns[0].options.break = true;

            textRuns[0].options.paraSpaceBefore = Math.round(block.blockMargin.top * 1.2);
            textRuns[textRuns.length - 1].options.paraSpaceAfter = Math.round(block.blockMargin.bottom * 1.2);
        }

        userSelectAttrs.forEach(userSelectAttr => preNode.style.setProperty(userSelectAttr, "none"));

        const textOptions = {
            ...this.convertRectToSlide(
                geom.Rect.FromBBox(containerNode.getBoundingClientRect())
                    .offset(-this.canvasBounds.left, -this.canvasBounds.top)
                    .multiply(1 / this.canvas.canvasScale)
            ),
            valign: "middle",
            fill
        };

        this.slide.addText(textRuns, textOptions);
    }

    exportText(textElement, slide) {
        if (textElement.model && textElement.model.formatOptions && (textElement.model.formatOptions.accountingStyle || textElement.model.formatOptions.changeStyle === CellChangeStyle.ARROWS)) {
            this.addWarning("Some formatting may be missing from the table slide.");
        }

        const TEXT_PADDING = 40;

        const textElementFont = textElement.font;

        const fontSize = textElement.textLayouter.fontSize(TextStyleEnum.REGULAR);
        const lineHeight = textElement.textLayout.lines[0].height;

        if (textElementFont.isCustomFont) {
            this.addWarning("Custom fonts may not export accurately.");
        }

        let textBounds = textElement.canvasBounds.removePadding({
            left: textElement.styles.paddingLeft ?? 0,
            right: textElement.styles.paddingRight ?? 0,
            top: textElement.styles.paddingTop ?? 0,
            bottom: textElement.styles.paddingBottom ?? 0
        });

        // give the text bounds a little padding to prevent undesired wrapping in export
        const textAlign = textElement.calculatedProps?.options?.textAlign || textElement.textLayouter.textAlign || "left";
        switch (textAlign) {
            case "left":
                textBounds = textBounds.addPadding({ right: TEXT_PADDING });
                break;
            case "right":
                textBounds = textBounds.addPadding({ left: TEXT_PADDING });
                break;
            case "center":
            default:
                textBounds = textBounds.addPadding({
                    left: TEXT_PADDING,
                    right: TEXT_PADDING
                });
                break;
        }

        const charSpacing = parseFloat((textElement.textLayouter.letterSpacing * fontSize).toFixed(1));
        const lineSpacing = lineHeight;

        const font = textElement.getFontForTextStyle("regular");
        // powerpoint applies lineheight to the first line of a text box which causes increases in lineheight to
        // push the text down. B.AI aligns the first line of the text to the exact top of the text bounds.
        // To adjust, we need to offset the text bounds up by the delta between the top of a PPT line and the letter H
        let offsetY = (font.tables.os2.sTypoAscender - font.charToGlyph("H").getMetrics().yMax) * fontSize / font.unitsPerEm;

        switch (font.name.split("-")[0].split(" ")[0]) {
            case "Montserrat":
                offsetY = 0 * fontSize / font.unitsPerEm;
                break;
            case "BebasNeue":
                offsetY = -60 * fontSize / font.unitsPerEm;
                break;
            case "RobotoSlab":
                offsetY = 0 * fontSize / font.unitsPerEm;
                break;
        }

        let verticalAlign = textElement.calculatedProps?.options?.verticalAlign || textElement.styles.verticalAlign || "top";
        verticalAlign = "middle";
        if (verticalAlign === "middle") {
            offsetY = 0;
        }

        // there is a little magic number adjustment here...
        textBounds.top -= 3;
        textBounds.top -= offsetY;
        textBounds.height += offsetY + 3;

        let fontColor = this.getColorFromRGB(textElement.fontColor);
        if (this.debug) {
            fontColor = "ff0000";
        }

        const boldFontColor = this.getColorFromRGB(textElement.boldFontColor);

        let textRuns = [];

        let currentRun = { text: "", options: { break: false }, isFirstWord: true };
        let paragraphIndex = 0;

        switch (textElement.paragraphStyle) {
            case "bullet_list":
                currentRun.options.bullet = true;
                break;
            case "numbered_list":
                currentRun.options.bullet = { type: "number" };
                break;
        }

        for (let lineIndex = 0; lineIndex < textElement.textLayout.lines.length; lineIndex++) {
            let line = textElement.textLayout.lines[lineIndex];
            for (let i = 0; i < line.words.length; i++) {
                let word = line.words[i];

                if (i == line.words.length - 1 && word.text == " " && textElement.paragraphStyle == "paragraph") {
                    continue; // skip trailing spaces at the end of a line because we are manually breakiing lines
                }

                let str = word.formattedText;
                switch (textElement.styles.textTransform) {
                    case "uppercase":
                        str = str.toUpperCase();
                        break;
                    case "lowercase":
                        str = str.toLowerCase();
                        break;
                    case "titlecase":
                        str = str.toTitleCase();
                        break;
                }

                let options = { break: false };

                if (word.style.includes("bold")) {
                    options.color = boldFontColor;
                    if (textElement.font.styles.some(style => style.weight > textElement.baseFontWeight)) {
                        options.bold = true;
                    }
                } else if (word.style.includes("italic")) {
                    if (textElement.hasItalicFont) {
                        options.italic = true;
                    }
                }

                if (word.link) {
                    let url = word.link;
                    if (!url.startsWith("http://") && !url.startsWith("https://")) {
                        url = "https://" + url;
                    }
                    options.hyperlink = { url: url };
                }

                // remove any line breaks
                str = str.replace(/(\r\n|\n|\r)/gm, "");
                // remove non printable chars that break export
                str = sanitizeString(str);

                if (str == "") continue;

                // check if we need to create a new run because styles have changed
                let needsNewRun = isObjectDiff(options, currentRun.options) && !currentRun.isFirstWord;
                // for paragraphs we add a break within each line of the paragraph so we can match linebreaks
                if (textElement.paragraphStyle == "paragraph" && lineIndex > 0 && i == 0 && line.paragraphIndex == paragraphIndex) {
                    // needsNewRun = true;
                    str = "\n" + str;
                }
                // for bullet and numbered list, we only create a new run if this is a new paragraph
                if (line.paragraphIndex != undefined && line.paragraphIndex != paragraphIndex) {
                    needsNewRun = true;

                    switch (textElement.paragraphStyle) {
                        case "bullet_list":
                            options.bullet = true;
                            options.break = undefined;
                            break;
                        case "numbered_list":
                            options.bullet = { type: "number" };
                            options.break = undefined;
                            break;
                        default:
                            options.break = true;
                    }
                }

                if (needsNewRun) {
                    // save the current run
                    if (currentRun) {
                        textRuns.push(currentRun);
                    }

                    // and create a new run
                    currentRun = { text: str, options: { ...options } };
                } else {
                    currentRun.text += str; // append text to current run
                    currentRun.options = Object.assign(currentRun.options, { ...options });
                    currentRun.isFirstWord = false;
                }

                paragraphIndex = line.paragraphIndex;
            }
        }

        textRuns.push(currentRun);

        let isBold = font.tables.os2.usWeightClass >= 700;

        let fontFace = font.names.fontFamily.en;
        if (fontFace == "Carlito") {
            if (font.name == "Carlito-Bold") {
                fontFace = "Calibri Bold";
                isBold = true;
            } else {
                fontFace = "Calibri";
            }
        }

        let textProps = {
            ...this.convertRectToSlide(textBounds),
            fontFace: fontFace,
            fontSize: Math.round(fontSize * 0.75), // convert pixels to points
            align: textAlign,
            color: fontColor,
            charSpacing: parseFloat((charSpacing * 0.75 * 1.2777).toFixed(2)),
            lineSpacing: parseFloat((lineSpacing * 0.75).toFixed(2)),
            // lineSpacing: Math.round(fontSize * 0.75),
            valign: verticalAlign,
            margin: 0,
            paraSpaceAfter: 6,
            bold: isBold
        };

        if (verticalAlign == "middle") {
            // ppt vertical align middle is bizarre and line spacing will result in off center
            // need to not send lineSpacing prop so that it defaults to 1.0
            delete textProps.lineSpacing;
        }

        if (textElement.styles && textElement.styles.rotate) {
            // this is pretty much here to handle the rotated y-axis label in the XYGraph
            textProps.rotate = textElement.styles.rotate;
            textProps.y -= textProps.w / 2;
            textProps.x -= textProps.w / 2 * .75;
        }

        let headlineContentBlock = textElement.findClosestOfType("HeadlineContentBlock");
        if (headlineContentBlock && headlineContentBlock.textStyle == "white_text_with_shadow") {
            textProps.shadow = {
                type: "outer",
                angle: 180,
                blur: 10,
                color: "000000",
                offset: 0.1,
                opacity: 0.3
            };
        }

        //If the text element is a slideNum the number should reflect the slide number in ppt.
        if (textElement.id === ELEMENT_IDS.SLIDE_NUM && Array.isArray(textRuns) && textRuns[0].text) {
            textRuns[0].text = slide.getPageNumber().toString();
        }

        this.slide.addText(textRuns, textProps);
    }

    exportSVGPolyline(element) {
        const polyline = new PolyLineExporter(this, element);
        return polyline.generate();
    }

    exportSVGShape(element, shapeType, svgNode = element.DOMNode.firstElementChild) {
        const shapeOptions = {
            ...this.convertRectToSlide(geom.Rect.FromBBox(svgNode.getBoundingClientRect()).offset(-this.canvasBounds.left, -this.canvasBounds.top).multiply(1 / this.canvas.canvasScale))
        };

        if (
            svgNode.style.fill &&
            svgNode.style.fill !== "transparent" &&
            svgNode.style.fill !== "none"
        ) {
            shapeOptions.fill = this.getSVGElementFillColor(svgNode);
        }
        if (
            svgNode.style.strokeWidth &&
            parseFloat(svgNode.style.strokeWidth) > 0 &&
            svgNode.style.stroke !== "none"
        ) {
            shapeOptions.line = this.getColorFromRGB(svgNode.style.stroke || "black");
            shapeOptions.lineSize = parseFloat(svgNode.style.strokeWidth);
        }

        if (element.styles && element.styles.shadow) {
            shapeOptions.shadow = {
                type: "outer",
                angle: 0,
                offset: 0.1,
                blur: parseFloat(element.styles.shadow.blur),
                color: "000000",
                opacity: parseFloat(element.styles.shadow.opacity)
            };
        }

        this.slide.addShape(shapeType, shapeOptions);
    }

    async exportVideo(element) {
        this.exportSVGShape(element.background, this.pptx.shapes.RECTANGLE);

        if (element.videoType === "upload") {
            const videoAsset = await ds.assets.getAssetById(element.model.videoAssetId, AssetType.VIDEO);
            const previewAssetId = videoAsset.get("previewAssetId");
            if (previewAssetId) {
                const previewAsset = await ds.assets.getAssetById(previewAssetId, AssetType.IMAGE);
                const previewUrl = await previewAsset.getURL("original", true);
                const imageData = await this.loadBase64Image(previewUrl);

                let imageBounds = new geom.Rect(0, 0, previewAsset.get("w"), previewAsset.get("h"));
                imageBounds = imageBounds.fitToSize(element.canvasBounds.size);
                imageBounds = imageBounds.centerInRect(element.canvasBounds);

                this.slide.addImage({
                    data: imageData,
                    ...this.convertRectToSlide(imageBounds)
                });

                this.addWarning("Uploaded videos are not currently supported and will be exported as static images.");
            } else {
                this.addWarning("Uploaded videos are not currently supported.");
            }
            return;
        }

        if (element.publicVideoUrl) {
            this.slide.addMedia({
                type: "online",
                link: element.publicVideoUrl,
                ...this.convertRectToSlide(element.canvasBounds)
            });

            this.slideNotes.push(`Video URL: ${element.publicVideoUrl}`);
        }
    }

    async exportSVGNodeAsImage(svgNode, bounds, padding = 20) {
        // SVG elements can exceed their bounds if they have stroked paths so we need to add padding to avoid clipping when converting to image

        if (!bounds) {
            bounds = geom.Rect.FromBBox(svgNode.getBoundingClientRect())
                .offset(-this.canvasBounds.left, -this.canvasBounds.top)
                .multiply(1 / this.canvas.canvasScale);
        }

        return new Promise(resolve => {
            const svgURL = new XMLSerializer().serializeToString(svgNode);
            const filtersURL = new XMLSerializer().serializeToString(document.getElementById("elements-filters"));
            const img = new Image();
            img.onload = () => {
                let imageData = getBase64Image(img);

                this.slide.addImage({
                    data: imageData,
                    ...this.convertRectToSlide(bounds.addPadding(padding))
                });

                resolve();
            };
            img.onerror = function(err) {
                logger.error(err);
            };
            let svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${bounds.width + padding * 2}' height='${bounds.height + padding * 2}'>`;
            // if the node has a clip path, add it
            if (svgNode.instance && svgNode.instance.clipper) {
                const clipPath = new XMLSerializer().serializeToString(svgNode.instance.clipper.node);
                svg += `<defs>${clipPath}</defs>`;
            }

            if (svgNode.instance && svgNode.instance.track && svgNode.instance.track()) {
                const serializedTrackPath = new XMLSerializer().serializeToString(svgNode.instance.track().node);
                svg += `<defs>${serializedTrackPath}</defs>`;
            }

            svg += `<g style="transform: translateX(${padding}px) translateY(${padding}px)">`;
            svg += filtersURL;
            svg += svgURL;
            svg += "</g></svg>";

            img.src = "data:image/svg+xml; charset=utf8, " + encodeURIComponent(svg);

            // $($("body").addEl(img)).css({position: "absolute", top: 0, left: 0});
        });
    }

    async exportRawSVGNode(svgNode) {
        const svg = new XMLSerializer().serializeToString(svgNode);
        const dataURL = "data:image/svg+xml;base64," + window.btoa(svg);

        const bounds = geom.Rect.FromBBox(svgNode.getBoundingClientRect())
            .offset(-this.canvasBounds.left, -this.canvasBounds.top)
            .multiply(1 / this.canvas.canvasScale);

        const imageOptions = {
            data: dataURL,
            preview: await this.loadBase64Image(dataURL),
            ...this.convertRectToSlide(bounds)
        };
        this.slide.addImage(imageOptions);
    }

    async exportSVGNode(element, svgNode = element.DOMNode.firstElementChild) {
        const originalCss = svgNode.style.cssText;

        let padding = 0;
        // svg bbox doesn't account for stroke so we need to pad the bbox by half the stroke width
        if (svgNode.style.strokeWidth && (svgNode.style.stroke && svgNode.style.stroke !== "none")) {
            padding = parseFloat(svgNode.style.strokeWidth) / 2;
        } else if (svgNode.getAttribute("stroke-width") && svgNode.getAttribute("stroke") && svgNode.getAttribute("stroke") !== "none") {
            padding = parseFloat(svgNode.getAttribute("stroke-width")) / 2;
        }

        // svg export doesn't support filters
        if (svgNode.style.filter) {
            svgNode.style.filter = null;
            this.addWarning("Drop shadow shape effects are not currently supported and were removed.");
        }

        let bounds = geom.Rect.FromBBox(svgNode.getBoundingClientRect())
            .offset(-this.canvasBounds.left, -this.canvasBounds.top)
            .multiply(1 / this.canvas.canvasScale)
            .inflate(padding);

        // if the node is an SVGElement, it may have a position set which we need to take into account
        if (element instanceof SVGElement && element.position) {
            bounds = bounds.offset(element.position);
        }

        if (element.styles && element.styles.padding) {
            bounds = bounds.offset(-element.styles.paddingLeft, -element.styles.paddingTop);
        }

        // Fix alpha in colors
        const fixAlpha = (svgNode, colorAttribute) => {
            if (!svgNode) {
                return;
            }

            // colors can be in styles
            if (svgNode.style) {
                const color = tinycolor(svgNode.style[colorAttribute]);
                const alpha = color.getAlpha();
                if (alpha !== 1) {
                    svgNode.style.setProperty(colorAttribute, color.setAlpha(1).toHexString());
                    let opacity = svgNode.style[`${colorAttribute}Opacity`];
                    if (opacity) {
                        opacity = parseFloat(opacity) * alpha;
                    } else {
                        opacity = alpha;
                    }
                    svgNode.style.setProperty(`${colorAttribute}-opacity`, opacity);
                }
            }

            // or colors can be in attributes
            if (svgNode.getAttribute) {
                const color = tinycolor(svgNode.getAttribute(colorAttribute));
                const alpha = color.getAlpha();
                if (alpha !== 1) {
                    svgNode.setAttribute(colorAttribute, color.setAlpha(1).toHexString());
                    let opacity = svgNode.getAttribute(`${colorAttribute}Opacity`);
                    if (opacity) {
                        opacity = parseFloat(opacity) * alpha;
                    } else {
                        opacity = alpha;
                    }
                    svgNode.setAttribute(`${colorAttribute}-opacity`, opacity);
                }
            }

            // recurse through child nodes
            if (svgNode.childNodes) {
                svgNode.childNodes.forEach(node => fixAlpha(node, colorAttribute));
            }
        };
        fixAlpha(svgNode, "fill");
        fixAlpha(svgNode, "stroke");

        // By some reason Powerpoint on mac hangs when there are some svg elements with the stroke-dasharray
        // parameter set to zero, so we have to remove it from the node and all its children
        const removeStrokeDashArray = svgNode => {
            if (!svgNode) {
                return;
            }

            // stroke-dasharray can be a style property
            if (svgNode.style && (svgNode.style.getPropertyValue("stroke-dasharray") == "0" || svgNode.style.getPropertyValue("stroke-dasharray") === "0px")) {
                svgNode.style.removeProperty("stroke-dasharray");
                svgNode.style.removeProperty("stroke-dashoffset");
            }
            // or an svg node attribute
            if (svgNode.getAttribute && (svgNode.getAttribute("stroke-dasharray") == "0" || svgNode.getAttribute("stroke-dasharray") === "0px")) {
                svgNode.removeAttribute("stroke-dasharray");
                svgNode.removeAttribute("stroke-dashoffset");
            }
            if (svgNode.childNodes) {
                svgNode.childNodes.forEach(node => removeStrokeDashArray(node));
            }
        };
        removeStrokeDashArray(svgNode);

        // Rounding w and h because Safari sometimes fails to load svg
        // images with non-integer w or h
        bounds.width = Math.round(bounds.width);
        bounds.height = Math.round(bounds.height);
        // Hack to avoid errors when exporting vertical or horizontal lines
        bounds.width = bounds.width === 0 ? 1 : bounds.width;
        bounds.height = bounds.height === 0 ? 1 : bounds.height;

        const bbox = geom.Rect.FromBBox(svgNode.getBBox()).inflate(padding);
        const viewBox = bbox.x + " " + bbox.y + " " + bbox.width + " " + bbox.height;
        const svg = `<svg viewBox='${viewBox}' xmlns='http://www.w3.org/2000/svg' width='${bounds.width}' height='${bounds.height}'>
            ${new XMLSerializer().serializeToString(svgNode)}
        </svg>`;

        const dataURL = "data:image/svg+xml;base64," + window.btoa(unescape(encodeURIComponent(svg)));
        const imageData = await this.loadBase64Image(dataURL);
        const slideImageBounds = this.convertRectToSlide(bounds);
        if (element.exportAsImage || element.options.exportAsImage) {
            this.slide.addImage({
                data: imageData,
                ...slideImageBounds
            });
        } else {
            this.slide.addImage({
                data: dataURL,
                preview: imageData,
                ...slideImageBounds
            });
        }

        // Restoring node styles
        svgNode.style.cssText = originalCss;
    }

    async exportFooter(footerElement) {
        const { logoBounds, logoFrameBounds } = footerElement.calculatedProps;

        if (logoFrameBounds) {
            const logoFrameCanvasBounds = logoFrameBounds.offset(footerElement.canvasBounds.position);

            const shapeOptions = {
                ...this.convertRectToSlide(logoFrameCanvasBounds),
                fill: "ffffff"
            };

            this.slide.addShape(this.pptx.shapes.RECTANGLE, shapeOptions);
        }

        if (logoBounds) {
            const logoUrl = await Logos.getSignedUrlAndLoad(footerElement.lastLogoPath, true);
            const logoImageData = await this.loadBase64Image(logoUrl);
            const logoCanvasBounds = logoBounds.offset(footerElement.canvasBounds.position);

            this.slide.addImage({
                data: logoImageData,
                rounding: false,
                x: this.convertCoordinate(logoCanvasBounds.left),
                y: this.convertCoordinate(logoCanvasBounds.top),
                w: this.convertCoordinate(logoCanvasBounds.width),
                h: this.convertCoordinate(logoCanvasBounds.height),
                flipH: false
            });
        }
    }

    async exportWordCloud(wordCloudElement) {
        const parentSvgNode = wordCloudElement.DOMNode.firstElementChild;
        for (const wordSvgNode of parentSvgNode.childNodes) {
            await this.exportSVGNode(wordCloudElement, wordSvgNode);
        }
    }

    async exportOrgChartConnectors(orgChartConnectorsElement) {
        const parentSvgNode = orgChartConnectorsElement.DOMNode.firstElementChild;
        for (const connectorSvgNode of parentSvgNode.childNodes) {
            await this.exportSVGNode(orgChartConnectorsElement, connectorSvgNode);
        }
    }

    async exportCanvasBackground(element) {
        if (!(element.background instanceof BackgroundImage)) {
            return;
        }

        const imageUrl = await element.background.getBackgroundImageUrl(true);
        if (!imageUrl) {
            return;
        }

        const imageData = await this.loadBase64Image(imageUrl);
        this.slide.addImage({
            data: imageData,
            x: 0,
            y: 0,
            w: this.convertCoordinate(CANVAS_WIDTH),
            h: this.convertCoordinate(CANVAS_HEIGHT),
        });
    }

    async exportStreetMap(streetMapElement) {
        const mapCanvasBounds = streetMapElement.map.canvasBounds;
        const mapSize = streetMapElement.map.mapSize + 30;

        const mapImageUrl = await streetMapElement.map.getAssetUrl(true);
        const imageData = await this.loadBase64Image(mapImageUrl);

        this.slide.addImage({
            data: imageData,
            rounding: false,
            x: this.convertCoordinate(mapCanvasBounds.left),
            y: this.convertCoordinate(mapCanvasBounds.top),
            w: this.convertCoordinate(mapSize),
            h: this.convertCoordinate(mapSize),
            sizing: {
                type: "crop",
                x: this.convertCoordinate(Math.abs(mapCanvasBounds.width - mapSize) / 2),
                y: this.convertCoordinate(Math.abs(mapCanvasBounds.height - mapSize) / 2),
                w: this.convertCoordinate(mapCanvasBounds.width),
                h: this.convertCoordinate(mapCanvasBounds.height)
            },
            flipH: false
        });

        await this.exportSVGNodeAsImage(streetMapElement.copyright.DOMNode, streetMapElement.copyright.canvasBounds);
    }

    async exportTable(tableElement) {
        const table = tableElement.table;

        await this.exportSVGNode(table.tableBackground);
        await this.exportSVGNode(table.tableGridLines);

        const font = table.fonts[table.styles.TableCell.fontId];
        const fontStyle = font.getStyle(table.styles.TableCell.fontWeight, false);

        let fontFace = fontStyle.label;
        if (fontFace == "Carlito") {
            fontFace = "Calibri";
        }

        for (let cell of table.cells) {
            if (cell.model.isHidden) continue;

            if (cell.format != FormatType.ICON) {
                let props = {
                    x: this.convertCoordinate(table.canvasBounds.left + cell.bounds.left),
                    y: this.convertCoordinate(table.canvasBounds.top + cell.bounds.top),
                    w: this.convertCoordinate(cell.bounds.width),
                    h: this.convertCoordinate(cell.bounds.height),
                    align: cell.styles.textAlign,
                    bold: cell.bold,
                    italic: cell.italic,
                    fontSize: cell.fontSize,
                    fontFace: fontFace,
                    shrinkText: true,
                    color: cell.styles.color ? this.getColorFromRGB(cell.styles.color) : null,
                    fill: (cell.styles.backgroundColor && cell.styles.backgroundColor != "transparent") ? this.getColorFromRGB(cell.styles.backgroundColor) : null,
                    line: cell.styles.border ? {
                        pt: "2",
                        color: this.getColorFromRGB(cell.styles.border.substr("solid 2px ".length))
                    } : null
                };

                let cellValue = cell.value || "";

                // some slides had tables with cells that contained a single Backspace char that blew up PPT export
                // it's not clear how to reproduce this state but i am special-case stripping them
                cellValue = cellValue.replace(/[\b]/g, "");

                if ((cell.format == FormatType.NUMBER || cell.format == FormatType.CURRENCY || cell.format == FormatType.PERCENT) && (cell.formatOptions && cell.formatOptions.changeStyle != CellChangeStyle.NONE)) {
                    if (parseFloat(cell.value) > 0) {
                        cellValue = "+" + cellValue;
                    }
                }

                if ((cell.formatOptions && cell.formatOptions.accountingStyle) ||
                    (cell.formatOptions && cell.formatOptions.changeStyle === CellChangeStyle.ARROWS) ||
                    cell.cellStyle == "flag" ||
                    cell.strikeThrough
                ) {
                    this.addWarning("Some table formatting options can not be exported.");
                }

                this.slide.addText(cellValue, props);
            }
        }

        for (let element of Object.values(table.elements)) {
            if (element.type == "TableCellIcon" && element.assetElement) {
                await this.exportElement(element);
            }
        }
    }

    async exportContent(contentElement) {
        if (contentElement.contentType == AssetType.ICON) {
            // let the iconElement export handle this
            return;
        }

        if (!contentElement.hasValidAsset) {
            return;
        }

        // if the image is flipped, we need to use the unflipped matrix or the image will be offset in PPT
        // so we temporarily switch image to unflipped and _calcSize to generate unflipped matrix
        const flipHorizontal = contentElement.model.flipHorizontal;
        if (flipHorizontal) {
            contentElement.model.flipHorizontal = false;
            contentElement.canvas.refreshElement(contentElement);
        }

        try {
            const primaryAsset = contentElement.assetElement.asset;
            let url;
            if (contentElement.contentType === AssetType.STOCK_VIDEO || contentElement.contentType === AssetType.VIDEO) {
                this.addWarning("Content videos are not currently supported and were exported as static images.");

                // upload
                if (primaryAsset) {
                    const previewAssetId = primaryAsset.get("previewAssetId");
                    if (previewAssetId) {
                        const previewAsset = await ds.assets.getAssetById(previewAssetId, AssetType.IMAGE);
                        url = await previewAsset.getURL("original", true);
                    }
                    // stock video
                } else {
                    url = contentElement.model.previewUrl;
                }

                if (contentElement.assetElement.publicVideoUrl) {
                    this.slideNotes.push(`Video URL: ${contentElement.assetElement.publicVideoUrl}`);
                }
            } else {
                if (primaryAsset.get("fileType") == "gif") {
                    this.addWarning("GIFs are not currently supported and were exported as static images.");
                }
                if ((contentElement.model.filter != null && contentElement.model.filter != "None") || contentElement.model.filterBlur != null || contentElement.model.filterContrast != null || contentElement.model.filterBrightness != null) {
                    this.addWarning("Image filters are not currently supported and were exported as unfiltered images.");
                }

                url = await primaryAsset.getURL("xlarge", true);
            }

            if (!url) {
                return;
            }

            const imageData = await this.loadBase64Image(url);

            const isCircle = contentElement.decoration && (contentElement.decoration.styles.shape == "circle" || contentElement.decoration.styles.shape == "octagon");
            let canvasBounds = contentElement.canvasBounds.deflate(contentElement.styles.padding);
            let offsetX = 0;
            let offsetY = 0;
            if (isCircle) {
                canvasBounds = canvasBounds.square();
                offsetX = (contentElement.canvasBounds.width - canvasBounds.width) / 2;
                offsetY = (contentElement.canvasBounds.height - canvasBounds.height) / 2;
            }

            this.slide.addImage({
                data: imageData,
                rounding: isCircle,
                x: this.convertCoordinate(canvasBounds.left + offsetX),
                y: this.convertCoordinate(canvasBounds.top + offsetY),
                w: this.convertCoordinate(contentElement.assetElement.mediaSize.width * contentElement.assetElement.calculatedProps.transformProps.scaleX),
                h: this.convertCoordinate(contentElement.assetElement.mediaSize.height * contentElement.assetElement.calculatedProps.transformProps.scaleX),
                sizing: {
                    type: "crop",
                    w: this.convertCoordinate(canvasBounds.width),
                    h: this.convertCoordinate(canvasBounds.height),
                    x: this.convertCoordinate(contentElement.assetElement.calculatedProps.transformProps.translateX * -1 + offsetX),
                    y: this.convertCoordinate(contentElement.assetElement.calculatedProps.transformProps.translateY * -1)
                },
                flipH: flipHorizontal
            });
        } finally {
            if (flipHorizontal) {
                contentElement.model.flipHorizontal = true;
                contentElement.canvas.refreshElement(contentElement);
            }
        }
    }

    exportBorderOverlay(borderOverlayElement) {
        const shapeOptions = {
            ...this.convertRectToSlide((borderOverlayElement.bounds).deflate(borderOverlayElement.calculatedBorderWidth / 4)),
            line: this.getColorFromRGB(borderOverlayElement.calculatedBorderColor),
            lineSize: borderOverlayElement.calculatedBorderWidth / 2
        };
        this.slide.addShape(this.pptx.shapes.RECTANGLE, shapeOptions);
    }

    async loadBase64Image(imageUrl) {
        const imageData = await new Promise((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => resolve(getBase64Image(image));
            image.onerror = reject;
            image.src = imageUrl;
        });

        return imageData;
    }
}

export { ExportToPPT };
